import { useCallback, useEffect, useState } from 'react';
import { Table } from '../../components/shared';
import { listUserAssignments, type AssignmentDto } from '../../api/hr-assignment';
import { getRecords, type RecordDetailDto } from '../../api/hr-records';
import { fetchUserAwards, type UserAwardsDto } from '../../api/hr-awards';
import { fetchUserEvaluations, type UserEvaluationDto } from '../../api/hr-evaluations';
import { fetchUserTrainings, type UserTrainingDto } from '../../api/hr-trainings';
// ★ 표의 «칸과 말»은 한 벌뿐이다 — 본인 카드(`MyRecordCardPage`)와 나눠 쓴다.
//   여기서 다시 그리면 두 화면이 또 갈라진다(실제로 갈라져 있었다).
import {
  ASSIGNMENT_COLUMNS, EDUCATION_COLUMNS, CAREER_COLUMNS, AWARD_COLUMNS,
  EVALUATION_COLUMNS, COURSE_COLUMNS, credentialColumns,
  courseRowClass, credentialRowClass,
} from './hrRecordTables';

/** 담당자 화면은 자격·어학을 **한 표**로 보므로 종류 칸을 세운다. */
const CREDENTIAL_COLUMNS = credentialColumns(true);

/**
 * 한 사람의 **인사기록 다섯** — 발령 · 경력/학력 · 포상/징계 · 인사평가 · 교육/자격/어학.
 *
 * ── 왜 뽑아냈나 (2026-08-31 · 시안 B2) ──────────────────────────────────
 * ★★★ 시안 B2(직원 상세)는 이 다섯을 **직원 관리 화면 안의 탭**으로 그린다. 그런데 그 내용은
 *    이미 «인사 카드»(`StaffCardPage`)가 갖고 있었다 — 두 화면이 같은 것을 그려야 하므로
 *    **한 벌만 두고 나눠 쓴다.** 복사해 두면 한쪽만 고쳐지는 날이 온다(이 저장소가 여러 번 겪었다).
 *
 * ★★ **탭을 열 때 그 탭의 것만 부른다** — 다섯을 한꺼번에 부르면 첫 화면이 그만큼 늦다.
 * ★★ **못 읽으면 그 탭만 접는다** — 화면 전체를 죽이지 않는다(범위 밖이면 서버가 막는다).
 * ★ 사람이 바뀌면 담아 둔 것을 **버린다** — 안 그러면 앞사람의 기록이 뒷사람 탭에 남는다.
 */
export type RecordTab = 'ASSIGNMENT' | 'RECORD' | 'AWARD' | 'EVALUATION' | 'TRAINING';

export const RECORD_TABS: ReadonlyArray<{ key: RecordTab; label: string }> = [
  { key: 'ASSIGNMENT', label: '인사발령' },
  { key: 'RECORD', label: '경력 · 학력' },
  { key: 'AWARD', label: '포상 · 징계' },
  { key: 'EVALUATION', label: '인사평가' },
  { key: 'TRAINING', label: '교육 · 자격 · 어학' },
];

export function StaffRecordSection({ userId, tab }: { userId: string; tab: RecordTab }): JSX.Element {
  const [assignments, setAssignments] = useState<AssignmentDto[] | null>(null);
  const [records, setRecords] = useState<RecordDetailDto | null>(null);
  const [awards, setAwards] = useState<UserAwardsDto | null>(null);
  const [evaluations, setEvaluations] = useState<UserEvaluationDto[] | null>(null);
  const [trainings, setTrainings] = useState<UserTrainingDto | null>(null);
  const [folded, setFolded] = useState<Partial<Record<RecordTab, boolean>>>({});

  // ★ 사람이 바뀌면 전부 버린다 — 앞사람의 기록을 뒷사람 탭에 남기지 않는다.
  useEffect(() => {
    setAssignments(null); setRecords(null); setAwards(null);
    setEvaluations(null); setTrainings(null); setFolded({});
  }, [userId]);

  const loadTab = useCallback(async (next: RecordTab) => {
    if (!userId) return;
    const fold = () => setFolded((f) => ({ ...f, [next]: true }));
    try {
      if (next === 'ASSIGNMENT' && assignments === null) {
        setAssignments((await listUserAssignments(userId)).assignments);
      } else if (next === 'RECORD' && records === null) {
        setRecords(await getRecords(userId));
      } else if (next === 'AWARD' && awards === null) {
        setAwards(await fetchUserAwards(userId));
      } else if (next === 'EVALUATION' && evaluations === null) {
        setEvaluations((await fetchUserEvaluations(userId)).rows);
      } else if (next === 'TRAINING' && trainings === null) {
        setTrainings(await fetchUserTrainings(userId));
      }
    } catch {
      fold();
    }
  }, [userId, assignments, records, awards, evaluations, trainings]);

  useEffect(() => { void loadTab(tab); }, [tab, loadTab]);

  if (folded[tab]) {
    return (
      <div className="hrpage__note">
        이 탭을 불러오지 못했습니다 — 볼 권한이 없거나 서버가 아직 준비되지 않았을 수 있습니다.
      </div>
    );
  }

  if (tab === 'ASSIGNMENT') {
    return (
      <section className="hrpage__panel">
        <h2 className="hrpage__panelt">
          인사발령
          <span className="hrpage__panels">시행된 것만 보입니다 — 예정은 아직 일어나지 않은 일입니다</span>
        </h2>
        <Table
          caption="인사발령" className="hrrec__tbl"
          columns={ASSIGNMENT_COLUMNS} rows={assignments ?? []} rowKey={(a) => a.id}
          loading={!assignments} empty="시행된 발령이 없습니다."
        />
      </section>
    );
  }

  if (tab === 'RECORD') {
    return (
      <>
        <section className="hrpage__panel">
          <h2 className="hrpage__panelt">
            학력<span className="hrpage__panels">{records?.educations.length ?? 0}건</span>
          </h2>
          <Table
            caption="학력" className="hrrec__tbl"
            columns={EDUCATION_COLUMNS} rows={records?.educations ?? []} rowKey={(e) => e.id}
            loading={!records} empty="등록된 학력이 없습니다."
          />
        </section>

        <section className="hrpage__panel">
          {/* ★ 「경력」이라고만 적으면 사내 이력으로 읽힌다 — 이 표는 **입사 전** 것이다. */}
          <h2 className="hrpage__panelt">
            입사 전 경력<span className="hrpage__panels">{records?.careers.length ?? 0}건</span>
          </h2>
          <Table
            caption="입사 전 경력" className="hrrec__tbl"
            columns={CAREER_COLUMNS} rows={records?.careers ?? []} rowKey={(c) => c.id}
            loading={!records} empty="등록된 경력이 없습니다."
          />
        </section>
      </>
    );
  }

  if (tab === 'AWARD') {
    return (
      <section className="hrpage__panel">
        <h2 className="hrpage__panelt">
          포상 · 징계
          <span className="hrpage__panels">말소된 기록도 남습니다 — 더는 불이익에 쓰지 않을 뿐입니다</span>
        </h2>
        <Table
          caption="포상 · 징계" className="hrrec__tbl"
          columns={AWARD_COLUMNS} rows={awards?.rows ?? []} rowKey={(a) => a.id}
          loading={!awards} empty="등록된 포상·징계가 없습니다."
        />
      </section>
    );
  }

  if (tab === 'EVALUATION') {
    return (
      <section className="hrpage__panel">
        <h2 className="hrpage__panelt">
          인사평가
          <span className="hrpage__panels">확정 전 회차는 등급 · 점수를 아직 말하지 않습니다</span>
        </h2>
        <Table
          caption="인사평가" className="hrrec__tbl"
          columns={EVALUATION_COLUMNS} rows={evaluations ?? []} rowKey={(e) => e.roundId}
          loading={!evaluations} empty="등록된 평가가 없습니다."
        />
      </section>
    );
  }

  return (
    <>
      <section className="hrpage__panel">
        <h2 className="hrpage__panelt">
          교육<span className="hrpage__panels">{trainings?.courses.length ?? 0}건</span>
        </h2>
        <Table
          caption="교육" className="hrrec__tbl"
          columns={COURSE_COLUMNS} rows={trainings?.courses ?? []} rowKey={(c) => c.id}
          rowClassName={courseRowClass}
          loading={!trainings} empty="등록된 교육이 없습니다."
        />
      </section>

      {/*
        ★ 자격과 어학은 **한 목록으로 온다**(`credentials`, `kind` 로 갈린다) — 서버가 그렇게
          정해 뒀다. 화면에서 둘로 쪼개면 서버가 종류를 하나 더 만들 때 어느 쪽에 붙일지 알 수 없다.
      */}
      <section className="hrpage__panel">
        <h2 className="hrpage__panelt">
          자격 · 어학<span className="hrpage__panels">{trainings?.credentials.length ?? 0}건</span>
        </h2>
        <Table
          caption="자격 · 어학" className="hrrec__tbl"
          columns={CREDENTIAL_COLUMNS} rows={trainings?.credentials ?? []} rowKey={(c) => c.id}
          rowClassName={credentialRowClass}
          loading={!trainings} empty="등록된 자격·어학이 없습니다."
        />
      </section>
    </>
  );
}

export default StaffRecordSection;
